import { isNative, isWeb } from "@stepapp/core";
import { memo, useCallback, useEffect, useState } from "react";
import { type StyleProp, StyleSheet, type ViewStyle } from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

import { useBreakpoints } from "../hooks/useBreakpoints";
import { palette, spacing } from "../styles";
import Block from "./Block";
import { Image } from "./Image";
import SvgIcon from "./SvgIcon";
import Touchable from "./Touchable";

export type CarouselItem = {
  id: string;
  desktopUrl: string;
  mobileUrl: string;
};

type CarouselProps = {
  data: CarouselItem[];
  style?: StyleProp<ViewStyle>;
  imageStyle?: StyleProp<ViewStyle>;
  autoRotateInterval?: number;
  width?: number | string;
  height?: number | string;
};

const Carousel = memo(
  ({
    data,
    style,
    imageStyle,
    autoRotateInterval = 5000,
    width = "100%",
    height = 200,
  }: CarouselProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [nextIndex, setNextIndex] = useState(1);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const { mobileOnly, md } = useBreakpoints();

    const image1TranslateX = useSharedValue(0);
    const image2TranslateX = useSharedValue(100);

    const performTransition = useCallback(
      (targetIndex: number, direction: "next" | "prev" = "next") => {
        setIsTransitioning(true);
        setNextIndex(targetIndex);

        const currentTranslateX = activeImageIndex === 0 ? image1TranslateX : image2TranslateX;
        const nextTranslateX = activeImageIndex === 0 ? image2TranslateX : image1TranslateX;

        nextTranslateX.value = direction === "next" ? 100 : -100;

        setTimeout(() => {
          currentTranslateX.value = withTiming(direction === "next" ? -100 : 100, {
            duration: 500,
            easing: Easing.out(Easing.cubic),
          });

          nextTranslateX.value = withTiming(0, {
            duration: 500,
            easing: Easing.out(Easing.cubic),
          });

          setTimeout(() => {
            setCurrentIndex(targetIndex);
            currentTranslateX.value = direction === "next" ? 100 : -100;
            setActiveImageIndex(activeImageIndex === 0 ? 1 : 0);
            setIsTransitioning(false);
          }, 500);
        }, 50);
      },
      [activeImageIndex, image1TranslateX, image2TranslateX],
    );

    const navigateToNext = () => {
      if (isTransitioning || data.length <= 1) return;

      const nextIdx = (currentIndex + 1) % data.length;
      performTransition(nextIdx, "next");
    };

    const navigateToPrevious = () => {
      if (isTransitioning || data.length <= 1) return;

      const prevIdx = currentIndex === 0 ? data.length - 1 : currentIndex - 1;
      performTransition(prevIdx, "prev");
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: skip
    useEffect(() => {
      if (data.length <= 1) return;

      const interval = setInterval(() => {
        if (isTransitioning) return;

        const nextIdx = (currentIndex + 1) % data.length;
        performTransition(nextIdx, "next");
      }, autoRotateInterval);

      return () => clearInterval(interval);
    }, [data.length, autoRotateInterval, performTransition]);

    useEffect(() => {
      setCurrentIndex(0);
      setNextIndex(data.length > 1 ? 1 : 0);
      setActiveImageIndex(0);
      setIsTransitioning(false);
      image1TranslateX.value = 0;
      image2TranslateX.value = 100;
    }, [data, image1TranslateX, image2TranslateX]);

    const image1AnimatedStyle = useAnimatedStyle(() => ({
      transform: [{ translateX: `${image1TranslateX.value}%` }],
    }));

    const image2AnimatedStyle = useAnimatedStyle(() => ({
      transform: [{ translateX: `${image2TranslateX.value}%` }],
    }));

    if (!data.length) {
      return null;
    }

    const currentItem = data[currentIndex];
    const nextItem = data[nextIndex];

    if (!currentItem) {
      return null;
    }

    const image1Item = activeImageIndex === 0 ? currentItem : nextItem;
    const image2Item = activeImageIndex === 0 ? nextItem : currentItem;

    const image1Url = image1Item ? (mobileOnly ? image1Item.mobileUrl : image1Item.desktopUrl) : "";
    const image2Url = image2Item ? (mobileOnly ? image2Item.mobileUrl : image2Item.desktopUrl) : "";

    return (
      <Block style={[styles.container, { width, height }, style]}>
        {image1Url && (
          <Animated.View style={[styles.imageContainer, styles.absoluteImage, image1AnimatedStyle]}>
            <Image
              source={{ uri: image1Url }}
              style={[styles.image, imageStyle]}
              width={width}
              height={height}
              contentFit="cover"
            />
          </Animated.View>
        )}

        {image2Url && (
          <Animated.View style={[styles.imageContainer, styles.absoluteImage, image2AnimatedStyle]}>
            <Image
              source={{ uri: image2Url }}
              style={[styles.image, imageStyle]}
              width={width}
              height={height}
              contentFit="cover"
            />
          </Animated.View>
        )}

        {isWeb && md && data.length > 1 && (
          <>
            <Touchable onPress={navigateToPrevious} style={styles.leftArrow}>
              <Block style={styles.arrowButton}>
                <SvgIcon name="chevron-left" color={palette.common.black} size={24} />
              </Block>
            </Touchable>

            <Touchable onPress={navigateToNext} style={styles.rightArrow}>
              <Block style={styles.arrowButton}>
                <SvgIcon name="chevron-right" color={palette.common.black} size={24} />
              </Block>
            </Touchable>
          </>
        )}

        {data.length > 1 && (
          <Block style={styles.indicatorContainer} row>
            {data.map((item, index) => (
              <Block
                key={item.id}
                style={[styles.indicator, index === currentIndex && styles.activeIndicator]}
              />
            ))}
          </Block>
        )}
      </Block>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    position: "relative",
    overflow: "hidden",
    borderRadius: isNative ? 0 : spacing.radius,
  },
  imageContainer: {
    width: "100%",
    height: "100%",
  },
  absoluteImage: {
    position: "absolute",
    top: 0,
    left: 0,
  },
  image: {
    width: "100%",
    height: "100%",
  },
  indicatorContainer: {
    position: "absolute",
    bottom: spacing.unit * 1.5,
    left: 48,
    right: 48,
    gap: isWeb ? 16 : 8,
  },
  indicator: {
    height: 4,
    flex: 1,
    borderRadius: 2,
    backgroundColor: palette.common.white,
    opacity: 0.5,
  },
  activeIndicator: {
    opacity: 0.9,
    backgroundColor: palette.common.white,
  },
  leftArrow: {
    position: "absolute",
    left: spacing.unit * 2,
    top: "50%",
    transform: [{ translateY: -20 }],
    zIndex: 2,
  },
  rightArrow: {
    position: "absolute",
    right: spacing.unit * 2,
    top: "50%",
    transform: [{ translateY: -20 }],
    zIndex: 2,
  },
  arrowButton: {
    width: spacing.unit * 4,
    height: spacing.unit * 4,
    borderRadius: 20,
    backgroundColor: palette.common.white,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default Carousel;
